import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {applySetDocumentDataFromSnapshot} from "../actions/documentDataActions";
import {applySetPartVersionDataCache} from "../actions/elementActions";
import {applyStopReceivingLatestDraftsForPartUid} from "../actions/miscActions";
import {applyAddNodes, applyRemoveNodes} from "../actions/pcbNodeActions";
import {applyRemoveGlobalPcbRuleSet, applySetNodeRules} from "../actions/pcbRulesActions";
import {buildDocumentStateUpdate, performDocumentMutation} from "../documentMutations";
import {routeActionToDraft} from "../legacyActionRouter";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type CompositeSliceState = Pick<
    DocumentStoreState,
    | "stopDraftsAndSetPartVersion"
    | "setPartVersionDataCacheBatch"
    | "restoreToDocumentVersion"
    | "deletePcbSubjectsBatch"
    | "batchMutate"
    | "dispatchBatch"
    | "moveTracesToLayerBatch"
>;

export function createCompositeSlice(deps: SliceDependencies): CompositeSliceState {
    const {primitives, set, get} = deps;

    return {
        stopDraftsAndSetPartVersion: (partUid, partVersionData) => {
            if (!partUid || !partVersionData) {
                return;
            }
            const document = get().document;
            if (!document?.elements || !document?.uid || !document?.pcbLayoutNodes) {
                return;
            }
            const elementsToChange = Object.values(document.elements).filter(
                (elementData) =>
                    elementData.part_uid === partUid &&
                    elementData.part_version_data_cache.version !== partVersionData.version,
            );
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => {
                        applyStopReceivingLatestDraftsForPartUid(draft, partUid);
                        if (elementsToChange.length > 0) {
                            applySetPartVersionDataCache(draft, elementsToChange, partVersionData);
                        }
                    },
                    {
                        actionType: "stopDraftsAndSetPartVersion",
                        canUndo: false,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                        additionalPayload: {
                            hasInheritedRules: true,
                            hasInheritedCalculations: true,
                            requiresDescendantRebake: true,
                            requiresNetDescendantRebake: true,
                        },
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        setPartVersionDataCacheBatch: (entries) => {
            if (!entries || entries.length === 0) {
                return;
            }
            const document = get().document;
            if (!document?.elements || !document?.uid || !document?.pcbLayoutNodes) {
                return;
            }
            try {
                performDocumentMutation(
                    primitives,
                    (draft) => {
                        for (const {partUid, partVersionData} of entries) {
                            const elementsToChange = Object.values(document.elements).filter(
                                (elementData) =>
                                    elementData.part_uid === partUid &&
                                    elementData.part_version_data_cache.version !== partVersionData.version,
                            );
                            if (elementsToChange.length > 0) {
                                applySetPartVersionDataCache(draft, elementsToChange, partVersionData);
                            }
                        }
                    },
                    {
                        actionType: "setPartVersionDataCacheBatch",
                        canUndo: false,
                        shouldGenerateActionRecord: true,
                        updatesDocumentTimestamp: true,
                        additionalPayload: {
                            hasInheritedRules: true,
                            hasInheritedCalculations: true,
                            requiresDescendantRebake: true,
                            requiresNetDescendantRebake: true,
                        },
                    },
                );
            } catch (error) {
                FluxLogger.captureError(error);
            }
        },

        restoreToDocumentVersion: (latestSnapshot, currentSnapshot) => {
            if (!latestSnapshot || !currentSnapshot) {
                return;
            }
            set(buildDocumentStateUpdate(latestSnapshot), false, "document/restoreToDocumentVersion/setData");
            performDocumentMutation(primitives, (draft) => applySetDocumentDataFromSnapshot(draft, currentSnapshot), {
                actionType: "restoreDocumentVersion",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: true,
                },
            });
        },

        deletePcbSubjectsBatch: (removeNodeArgs, ruleSetUids) => {
            if (!removeNodeArgs && (!ruleSetUids || ruleSetUids.length === 0)) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    if (removeNodeArgs) {
                        applyRemoveNodes(
                            draft,
                            removeNodeArgs.nodeUids,
                            removeNodeArgs.toggleBakedNodes ?? [],
                            removeNodeArgs.updatedNodesRulesSetsMap ?? {},
                        );
                    }
                    if (ruleSetUids && ruleSetUids.length > 0) {
                        applyRemoveGlobalPcbRuleSet(draft, ruleSetUids);
                    }
                },
                {
                    actionType: "deletePcbSubjectsBatch",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        batchMutate: (mutate, actionType, options) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, mutate, {
                actionType,
                canUndo: options.canUndo,
                shouldGenerateActionRecord: options.shouldGenerateActionRecord,
                updatesDocumentTimestamp: options.updatesDocumentTimestamp,
                additionalPayload: options.additionalPayload,
            });
        },

        dispatchBatch: (actions) => {
            const document = get().document;
            if (!document) {
                return;
            }
            if (actions.length === 0) {
                return;
            }
            const firstAction = actions[0]!;
            const canUndo = actions.some((a) => a.payload?.canUndo);
            const shouldGenerateActionRecord = actions.some((a) => a.payload?.shouldGenerateActionRecord);
            const updatesDocumentTimestamp = actions.some((a) => a.payload?.updatesDocumentTimestamp);
            performDocumentMutation(
                primitives,
                (draft) => {
                    for (const action of actions) {
                        routeActionToDraft(draft, action, document);
                    }
                },
                {
                    actionType: firstAction.type,
                    canUndo,
                    shouldGenerateActionRecord,
                    updatesDocumentTimestamp,
                    additionalPayload: firstAction.payload,
                },
            );
        },

        moveTracesToLayerBatch: (nodeRuleUpdates, removeNodeUids, addNodesMap) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    if (Object.keys(nodeRuleUpdates).length > 0) {
                        applySetNodeRules(draft, nodeRuleUpdates);
                    }
                    if (removeNodeUids.length > 0) {
                        applyRemoveNodes(draft, removeNodeUids, [], {});
                    }
                    if (Object.keys(addNodesMap).length > 0) {
                        applyAddNodes(draft, addNodesMap, document);
                    }
                },
                {
                    actionType: "moveTracesToLayerBatch",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: {
                        hasInheritedRules: true,
                        hasInheritedCalculations: true,
                        requiresDescendantRebake: true,
                        requiresNetDescendantRebake: true,
                    },
                },
            );
        },
    };
}
